<template>
  <div class="commutator-gauge">
    <e-chart
      class="commutator-gauge__chart"
      autoresize
      :options="options"
    />
    <span class="commutator-gauge__btn-wrapper">
      <s-loading-button
        v-show="$hasUserPermission('PER013')"
        class="commutator-gauge__btn"
        variant="outline-primary"
        size="sm"
        :loading="loadingReset"
        @click="reset"
      >
        {{ $t('actions.reset') }}
      </s-loading-button>
    </span>
  </div>
</template>

<script>
import themeService from '@/services/themeService';
import { formatter as dateTimeFormatter } from '@/services/dateTime/datetimeService';
import engineeringEvents from '@/domain/events/engineering';
import eventBus from '@/services/globalEventBus';

const MAXIMUM_VALUE = 'maximum-value';
const PRINT_FONT_COLOR = '#4F4F4F';

export default {
  name: 'CommutatorGauge',
  props: {
    reading: {
      type: Object,
      default: () => ({}),
    },
    minMaxValues: {
      type: Object,
      default: () => ({
        min: 0,
        max: 200,
      }),
    },
    isTransformer: {
      type: Boolean,
      default: false,
    },
    loadingReset: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    gaugeFontColor: themeService.getFontColor(),
    gaugeCurrentPointerColor: themeService.getStyleVar('color-close'),
    tooltipListener: null,
  }),
  computed: {
    existCurrentValue() {
      const currentValue = this.reading?.currentValue;
      return typeof currentValue === 'number';
    },
    options() {
      return {
        tooltip: {
          trigger: 'item',
          borderColor: themeService.getStyleVar('color-tooltip-border'),
          formatter: this.tooltipFormatter,
        },
        series: [
          {
            markLine: {
              label: {
                show: true,
              },
            },
            type: 'gauge',
            detail: {
              show: true,
            },
            min: this.minMaxValues.min,
            max: this.minMaxValues.max,
            axisLine: {
              lineStyle: {
                color: [[1, this.gaugeFontColor]],
                width: 1,
              },
            },
            axisLabel: {
              distance: 5,
              fontSize: 10,
              color: this.gaugeFontColor,
            },
            axisTick: {
              length: 5,
              distance: 0,
              lineStyle: {
                color: this.gaugeFontColor,
              },
            },
            splitLine: {
              distance: 0,
              length: 13,
              lineStyle: {
                width: 1.5,
                color: this.gaugeFontColor,
              },
            },
            pointer: {
              length: '80%',
              width: 6,
            },
            data: [
              {
                value: this.reading.currentValue,
                name: `${this.$n(this.reading.currentValue, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 3,
                })} °c`,
                detail: {
                  show: false,
                  color: this.gaugeFontColor,
                },
                itemStyle: {
                  color: this.gaugeCurrentPointerColor,
                },
                title: {
                  show: this.existCurrentValue,
                  fontSize: 20,
                  offsetCenter: [0, '40%'],
                  color: this.gaugeFontColor,
                },
              },
            ],
          }, {
            markLine: {
              label: {
                show: false,
              },
            },
            type: 'gauge',
            axisLine: {
              show: false,
            },
            splitLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              show: false,
            },
            detail: {
              show: false,
            },
            min: this.minMaxValues.min,
            max: this.minMaxValues.max,
            pointer: {
              length: '70%',
              width: 3,
            },
            data: [
              {
                id: MAXIMUM_VALUE,
                value: this.reading.maximumStoredValue,
                name: this.isTransformer
                  ? this.$t('engineering.transformer')
                  : `${this.$t('engineering.commutator')} ${this.reading.commutatorId}`,
                title: {
                  show: true,
                  fontSize: 14,
                  offsetCenter: [0, '-40%'],
                  color: this.gaugeFontColor,
                },
                itemStyle: {
                  color: themeService.getStyleVar('color-danger-dark'),
                },
              },
            ],
          },
        ],
      };
    },
  },
  created() {
    this.onCreateConfigPrintEvents();
    this.onCreateConfigSystemPrintEvents();
  },
  methods: {
    reset() {
      this.$confirmMessage({ code: 'MC047' }).accepted(() => {
        this.$emit('reset', this.reading.commutatorId);
      });
    },
    tooltipFormatter(params, ticket, callback) {
      if (this.tooltipListener) {
        clearTimeout(this.tooltipListener);
      }

      const { maximumValueDateTime } = this.reading;
      if (!maximumValueDateTime || params.data.id !== MAXIMUM_VALUE) {
        return '';
      }
      const tooltipValue = dateTimeFormatter(this.reading.maximumValueDateTime);
      this.tooltipListener = setTimeout(() => {
        callback(ticket, tooltipValue);
      }, 500);
      return this.$t('general.loading');
    },
    onSystemPrint() {
      this.gaugeFontColor = PRINT_FONT_COLOR;
      this.gaugeCurrentPointerColor = PRINT_FONT_COLOR;
    },
    onCreateConfigSystemPrintEvents() {
      eventBus.on(engineeringEvents.print, this.onSystemPrint, this);
    },
    onCreateConfigPrintEvents() {
      const self = this;
      window.addEventListener('beforeprint', () => {
        self.gaugeFontColor = PRINT_FONT_COLOR;
        self.gaugeCurrentPointerColor = PRINT_FONT_COLOR;
      });
      window.addEventListener('afterprint', () => {
        self.gaugeFontColor = themeService.getFontColor();
        self.gaugeCurrentPointerColor = themeService.getStyleVar('color-close');
      });
    },
  },
};
</script>

<style lang="sass" scoped>
.commutator-gauge
  position: relative
  display: inline-flex
  justify-content: center
  width: 300px
  height: 300px
  &__chart
    width: 300px
    height: 300px
  &__btn-wrapper
    position: absolute
    width: min-content
    bottom: 50px
  &__btn
    height: 25px
    padding: 0 8px
</style>
